import { TextField } from '@atgfr8/atg-ui-tailwind';
import { PlusIcon } from '@atgfr8/icons';
import AtgGifLoader from '@common/AtgGifLoader';
import { SelectInput, ToastType, PhoneInput } from '@components';
import { Button } from '@components/Button';
import { createForm } from '@felte/solid';
import { validator } from '@felte/validator-yup';
import BottomSheet, { BottomSheetAPI } from '@mobileComponents/BottomSheet';
import {
  customerDetailsStore,
  updateCustomerDetailsState,
} from '@mobileStore/customer';
import { getCustomerUserNameExists } from '@store/customers/customerDetails';
import { CustomerDetailsContact } from '@typeDefinitions/customerTypes';
import { formatDataTestId } from '@utils/dataTestIdFormatter';
import { normalizeWhitespace } from '@utils/formUtils';
import { handleToast } from '@utils/utils';
import { debounce, isEmpty } from 'lodash';
import { createSignal, onMount, Show } from 'solid-js';

import { contactSchema } from './validation';

export type ContactFormValues = {
  id?: number;
  name: string;
  type: string;
  phone1: string;
  phoneVerified: boolean;
  extension1: string | null;
  phone2: string;
  extension2: string | null;
  fax: string | null;
  email: string;
  emailVerified: boolean;
  cpn: string;
  createdDate: string;
  uniqueId: number | null;
  objectType: string;
  verifiedDisabled: boolean;
  verificationStatus: number;
  userName: string | null;
  password: string | null;
  phone?: string;
  mailerEmails?: boolean;
  operationType?: string;
};

type Props = {
  contactId?: string | number;
  isOpen?: boolean;
  onClose?: () => void;
};

const AddEditContact = (props: Props) => {
  let sheetAPI: BottomSheetAPI | undefined;
  const [isUserNameLoading, setIsUserNameLoading] = createSignal(false);
  const [userNameDuplicateError, setUserNameDuplicateError] = createSignal<
    string | null
  >(null);

  const { form, errors, data, isSubmitting, setIsSubmitting, setData } =
    createForm<ContactFormValues>({
      initialValues: {
        name: '',
        type: '',
        phone1: '',
        phoneVerified: false,
        extension1: null,
        phone2: '',
        extension2: null,
        fax: null,
        email: '',
        uniqueId: -1,
        emailVerified: false,
        userName: null,
        password: null,
      },
      extend: validator({ schema: contactSchema }),
      onSubmit: (values) => {
        handleFormSubmit(values);
      },
    });

  const loadContactData = () => {
    const contactList = customerDetailsStore.customer.contacts;
    const contactDetails: CustomerDetailsContact | undefined = contactList.find(
      (contact) => contact.id === Number(props.contactId),
    );
    if (contactDetails) {
      setData('name', contactDetails.name);
      setData('type', contactDetails.type);
      setData('phone1', contactDetails.phone1 ?? '');
      setData('extension1', contactDetails.extension1);
      setData('phone2', contactDetails.phone2 ?? '');
      setData('extension2', contactDetails.extension2);
      setData('email', contactDetails.email ?? '');
      setData('emailVerified', contactDetails.emailVerified);
      setData('fax', contactDetails.fax);
      setData('phoneVerified', contactDetails.phoneVerified);
      setData('createdDate', contactDetails.createdDate);
      setData('uniqueId', contactDetails.uniqueId);
      setData('id', contactDetails.id);
      setData('operationType', contactDetails.operationType ?? 'None');
      setData('userName', contactDetails.userName ?? null);
      setData('password', contactDetails.password ?? null);
    }
  };

  onMount(() => {
    if (props.contactId) {
      loadContactData();
    }
  });

  const handleLoginName = debounce(async (value: string) => {
    const payload = {
      username: value,
      customerId: customerDetailsStore.customer.id!,
      customercontactId: 0,
    };
    setIsUserNameLoading(true);
    const res = await getCustomerUserNameExists(payload);
    const duplicateUserName = customerDetailsStore.customer.contacts.find(
      (contact) => contact.userName?.toLowerCase() === value.toLowerCase(),
    );
    if ((res && res.isSuccess && Boolean(res.value)) || duplicateUserName) {
      setUserNameDuplicateError(
        `${value} already exists in the system as a username. Please choose another one.`,
      );
    } else {
      setUserNameDuplicateError(null);
    }
    setIsUserNameLoading(false);
  }, 500);

  const handleFormSubmit = (values: ContactFormValues) => {
    const payload = { ...values };

    try {
      setIsSubmitting(true);
      handleCustomerContactData(payload);
      handleClose();
    } catch (error) {
      handleToast(
        ToastType.Error,
        `Failed to ${props.contactId ? 'Update' : 'Add New'} Contact`,
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCustomerContactData = (
    values: Partial<CustomerDetailsContact>,
  ) => {
    const updatedValues = {
      ...values,
      phone: values.phone1,
      operationType: !Boolean(values.id) ? 'Insert' : values.operationType,
    };

    const contacts = customerDetailsStore.customer.contacts.map((contact) =>
      contact.id === values.id
        ? {
            ...contact,
            ...updatedValues,
            operationType:
              values.operationType !== 'Insert' ? 'Update' : 'Insert',
          }
        : contact,
    );

    if (!contacts.some((contact) => contact.id === values.id)) {
      const nextId = Math.max(...contacts.map((c) => c.id), 0) + 1;
      contacts.push({
        ...updatedValues,
        id: nextId,
      } as CustomerDetailsContact);
    }
    //NOTE: Type assertion used above as Contact to avoid adding all fields of Contact type in the form
    updateCustomerDetailsState({
      contacts: contacts as CustomerDetailsContact[],
    });
  };
  const handleClose = () => {
    if (props.contactId) {
      props.onClose?.();
    } else {
      sheetAPI?.close();
    }
  };

  const isEditMode = Boolean(props.contactId);
  const drawerTitle = isEditMode ? 'Edit Contact' : 'Add Contact';

  return (
    <>
      <BottomSheet
        title={drawerTitle}
        ref={(api) => (sheetAPI = api)}
        isOpen={isEditMode ? props.isOpen : undefined}
        onClose={isEditMode ? handleClose : undefined}
        trigger={
          <Show when={!isEditMode}>
            <Button
              variant="contained"
              color="info"
              label="Add"
              size="sm"
              class="bg-white! text-[#026ea1]!"
              startIcon={<PlusIcon size={20} />}
              dataTestId="customer-info-add-contact"
            />
          </Show>
        }
      >
        <Show when={isUserNameLoading()}>
          <AtgGifLoader />
        </Show>
        <form ref={form} style={{ width: '100%' }}>
          <div class="flex flex-col gap-2.5">
            <div>
              <TextField
                label="Name"
                error={errors().name}
                required
                value={data().name}
                onChange={(e: unknown) =>
                  setData('name', normalizeWhitespace(e as string))
                }
                maxLength={50}
                dataTestId={'name-textInput'}
                fullWidth
                class="font-normal!"
              />
            </div>
            <div>
              <SelectInput
                label="Type"
                placeholder="Select Type"
                menuItems={['Billing', 'Freight', 'EDI', 'Collections'].map(
                  (t) => ({ label: t, value: t }),
                )}
                value={data().type}
                onChange={(val) => setData('type', val)}
                error={(() => {
                  const err = errors().type;
                  if (err == null) return null;
                  if (Array.isArray(err)) return err[0] ?? null;
                  return typeof err === 'string' ? err : null;
                })()}
                dataTestId={formatDataTestId(
                  'Mobile customer contact type',
                  'selectInput',
                )}
                class="font-normal! [&~label]:font-semibold!"
              />
            </div>
            <div class="grid grid-cols-12 gap-2">
              <div class="col-span-8">
                <PhoneInput
                  label="Phone Number"
                  value={data().phone1}
                  onChange={(e) => setData('phone1', e)}
                  error={errors().phone1}
                  dataTestId="new-prospect-modal-contact-phone-phoneInput"
                  class="font-normal!"
                  maxDigits={20}
                />
              </div>
              <div class="col-span-4">
                <TextField
                  label="Ext1."
                  type="number"
                  value={data().extension1 ?? ''}
                  onChange={(e: unknown) => setData('extension1', e as string)}
                  error={errors().extension1}
                  maxLength={4}
                  dataTestId={'ext1-textInput'}
                  fullWidth
                  class="font-normal!"
                />
              </div>
            </div>
            <div class="grid grid-cols-12 gap-2">
              <div class="col-span-8">
                <PhoneInput
                  label="Alt"
                  value={data().phone2}
                  onChange={(e) => setData('phone2', e)}
                  error={errors().phone2}
                  dataTestId="alt-phone-phoneInput"
                  class="font-normal!"
                  maxDigits={20}
                />
              </div>
              <div class="col-span-4">
                <TextField
                  label="Ext2."
                  type="number"
                  value={data().extension2 ?? ''}
                  onChange={(e: unknown) => setData('extension2', e as string)}
                  error={errors().extension2}
                  maxLength={4}
                  dataTestId={'ext2-textInput'}
                  fullWidth
                  class="font-normal!"
                />
              </div>
            </div>
            <div>
              <PhoneInput
                label="Fax"
                value={data().fax ?? ''}
                onChange={(e) => setData('fax', e)}
                error={errors().fax}
                dataTestId="new-prospect-modal-contact-fax-phoneInput"
                class="font-normal!"
              />
            </div>
            <div>
              <TextField
                label="Email"
                type="email"
                value={data().email}
                onChange={(val: string) => {
                  if (!val) {
                    setData('mailerEmails', false);
                  }
                  setData('email', val);
                }}
                error={errors().email}
                dataTestId={'email-textInput'}
                fullWidth
                class="font-normal!"
              />
            </div>
            <Show when={['Billing', 'Freight'].includes(data().type)}>
              <div>
                <TextField
                  label="Login Name"
                  value={data().userName ?? ''}
                  onChange={(val: unknown) => {
                    const trimmed = (val as string).trimStart();
                    setData('userName', trimmed);
                    setUserNameDuplicateError(null);
                    if (!isEmpty(trimmed)) {
                      void handleLoginName(trimmed);
                    }
                  }}
                  error={errors().userName || userNameDuplicateError()}
                  dataTestId={'login-name-textInput'}
                  autocomplete="off"
                  fullWidth
                  fontSize="16px"
                  class="font-normal!"
                />
              </div>
              <div>
                <TextField
                  type="password"
                  label="Password"
                  value={data().password ?? ''}
                  onChange={(val: unknown) =>
                    setData('password', val as string)
                  }
                  error={errors().password}
                  dataTestId={'password-textInput'}
                  autocomplete="new-password"
                  fullWidth
                  fontSize="16px"
                  class="font-normal!"
                />
              </div>
            </Show>
            <div>
              <Button
                startIcon={<PlusIcon size={20} />}
                label={props.contactId ? 'Update Contact' : 'Add Contact'}
                isLoading={isSubmitting()}
                disabled={isSubmitting() || Boolean(userNameDuplicateError())}
                type="submit"
                dataTestId="customer-info-addor-update-contact"
                variant="contained"
                class="w-full p-1! text-[14px]!"
              />
            </div>
          </div>
        </form>
      </BottomSheet>
    </>
  );
};

export default AddEditContact;
